import {StyleSheet} from "aphrodite";
import * as React from "react";

import {IntegerField} from "@khanacademy/event-schemas";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Toolbar from "@khanacademy/wonder-blocks-toolbar";
import Tooltip, {TooltipContent} from "@khanacademy/wonder-blocks-tooltip";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {doNotTranslateYet} from "@khan/i18n";
import {PermissionsInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import {Flags} from "#clarifications-package/clear-flags-controls.tsx";
import FlagControls from "#clarifications-package/flag-controls.tsx";
import VoteControls from "#clarifications-package/vote-controls.tsx";
import BottomSheet from "#components/bottom-sheet/bottom-sheet.tsx";
import type {ComputingCodeMultiFile} from "#content-library/types/types.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import PythonSpinOffButton from "../python-spin-off-button.tsx";

import AdminSettingsButton from "./admin-settings-button.tsx";
import HideButtons from "./hide-buttons.tsx";
import NewProgramButton from "./new-program-button.tsx";
import type {UserProgram} from "./use-user-program.ts";

import dotsThreeVerticalIcon from "@phosphor-icons/core/bold/dots-three-vertical-bold.svg";
import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";

type Props = {
    program: UserProgram;
    profile: ProfileModelType | null | undefined;
    runButton: React.ReactNode;
    code: ComputingCodeMultiFile;
    saveButtonProps?: {
        disabled: boolean;
        spinner: boolean;
        onClick: () => void;
    };
    useEmbeddedStyles?: boolean;
};

export default function Footer({
    program,
    profile,
    runButton,
    code,
    saveButtonProps,
    useEmbeddedStyles,
}: Props): React.ReactElement {
    const isModerator = profile?.get("isModerator");
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const interactButtons: Array<React.ReactNode> = [];
    if (!program.isOwner) {
        interactButtons.push(
            <VoteControls
                key="vote"
                scratchpadKey={program.key}
                sumVotesIncremented={program.sumVotesIncremented}
                upVoted={program.upVoted}
            />,
        );
    }
    if (!isModerator && !program.isOwner) {
        interactButtons.push(
            <FlagControls
                key="flag"
                id={program.key}
                flaggedByUser={program.isFlaggedByCurrentUser}
                flagButton={true}
            />,
        );
    }

    const adminAndModeratorButtons: Array<React.ReactNode> = [];
    if (PermissionsInfo.hasAnyPermissions) {
        adminAndModeratorButtons.push(
            <AdminSettingsButton
                key="category"
                programId={program.id}
                category={program.category}
            />,
        );
    }
    if (isModerator) {
        adminAndModeratorButtons.push(
            <HideButtons
                key="hide"
                programId={program.id}
                hiddenEverywhere={program.hiddenEverywhere}
                hiddenFromHotlist={program.hiddenFromHotlist}
            />,
        );
    }
    if (
        isModerator &&
        !program.definitelyNotSpam &&
        program.flags != null &&
        program.flags.length > 0
    ) {
        adminAndModeratorButtons.push(
            <Flags
                key="review-flags"
                flaggedBy={program.flaggedByKaids}
                scratchpadKey={program.key}
                flags={program.flags}
                inQueue={false}
                definitelyNotSpam={program.definitelyNotSpam ?? false}
                shouldUseReviewModal={true}
                buttonKind="secondary"
            />,
        );
    }
    const styles = useEmbeddedStyles ? embeddedStyles : standaloneStyles;
    const renderCreateProgramButtons = (showTooltips: boolean) => {
        const handleSpinoffSaveClicked = () => {
            const programIdInt = parseInt(program.id);

            publishAnalyticsEvent({
                eventType: "ComputingUserProgramSpinoffSubmitted",
                eventSchemaVersion: 3,
                originProgramId: new IntegerField(programIdInt),
                userAuthoredContentType: "PYTHON",
            });
        };

        return (
            <>
                <Tooltip
                    opened={showTooltips ? undefined : false}
                    content={
                        <View style={styles.tooltipContent}>
                            <TooltipContent>
                                {doNotTranslateYet`Create your own program! Start from scratch and see where your creativity takes you.`}
                            </TooltipContent>
                        </View>
                    }
                >
                    <NewProgramButton
                        curationNodeSlug={program.parentCurationNode.slug}
                        icon={showTooltips ? infoIcon : undefined}
                    />
                </Tooltip>
                <Tooltip
                    opened={showTooltips ? undefined : false}
                    content={
                        <View style={styles.tooltipContent}>
                            <TooltipContent>
                                {doNotTranslateYet`Copy this program to put your own unique twist on it! Your spin-off will link back here to credit the original creator.`}
                            </TooltipContent>
                        </View>
                    }
                >
                    <PythonSpinOffButton
                        originProgramOrContentId={program.id}
                        originProgramKind="PROGRAM"
                        originRevisionId={program.latestRevision.id}
                        curationNodeSlug={program.parentCurationNode.slug}
                        code={code}
                        icon={showTooltips ? infoIcon : undefined}
                        onSpinoffSaveClicked={handleSpinoffSaveClicked}
                    />
                </Tooltip>
            </>
        );
    };

    const mobileBottomSheetButtons = (
        <View style={styles.bottomSheetButtons}>
            {interactButtons.length > 0 && (
                <View
                    style={[
                        styles.mobileInteractButtons,
                        {
                            gridTemplateColumns: `repeat(${interactButtons.length}, 1fr)`,
                        },
                    ]}
                >
                    {interactButtons}
                </View>
            )}
            {renderCreateProgramButtons(false)}
        </View>
    );

    return (
        <View style={styles.footer}>
            <Toolbar
                leftContent={
                    <View style={styles.desktopAdminButtons}>
                        {adminAndModeratorButtons}
                    </View>
                }
                rightContent={
                    <>
                        <View style={styles.desktopFooterButtons}>
                            {interactButtons.length > 0 && (
                                <>
                                    {interactButtons}
                                    <View
                                        style={styles.footerButtonsSeparator}
                                    />
                                </>
                            )}
                            {renderCreateProgramButtons(true)}
                        </View>
                        <View style={styles.mobileFooterButtons}>
                            {[...adminAndModeratorButtons].reverse()}
                            <View
                                style={[
                                    styles.primaryMobileFooterButtons,
                                    saveButtonProps == null
                                        ? styles.primaryMobileFooterButtonsWithoutSaveButton
                                        : styles.primaryMobileFooterButtonsWithSaveButton,
                                ]}
                            >
                                {runButton}
                                {saveButtonProps != null && (
                                    <Button {...saveButtonProps}>
                                        {doNotTranslateYet`Save`}
                                    </Button>
                                )}

                                <BottomSheet content={mobileBottomSheetButtons}>
                                    {({openModal}) => (
                                        <IconButton
                                            kind="tertiary"
                                            actionType="neutral"
                                            icon={dotsThreeVerticalIcon}
                                            aria-label={doNotTranslateYet`More options`}
                                            onClick={openModal}
                                        />
                                    )}
                                </BottomSheet>
                            </View>
                        </View>
                    </>
                }
            />
        </View>
    );
}

/* eslint-disable react-native/no-unused-styles */
const getStyles = (useEmbeddedStyles: boolean) =>
    StyleSheet.create({
        footer: {
            flexShrink: 0,
        },
        bottomSheetButtons: {
            display: "flex",
            flexDirection: "column",
            gap: spacing.small_12,
        },
        footerButtonsSeparator: {
            width: 1,
            backgroundColor: color.offBlack16,
        },
        desktopFooterButtons: {
            display: "flex",
            flexDirection: "row",
            gap: spacing.medium_16,
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "none",
            },
        },
        desktopAdminButtons: {
            display: "flex",
            flexDirection: "row",
            gap: spacing.medium_16,
            [useEmbeddedStyles
                ? mediaQueries.smOrSmaller
                : mediaQueries.mdOrSmaller]: {
                display: "grid",
                gridTemplateColumns: "auto auto",
                gridRowGap: spacing.small_12,
                paddingTop: spacing.small_12,
                paddingBottom: spacing.small_12,
            },
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "none",
            },
        },
        mobileFooterButtons: {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: spacing.small_12,
            paddingTop: spacing.small_12,
            paddingBottom: spacing.small_12,
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                display: "none",
            },
        },
        mobileInteractButtons: {
            display: "grid",
            gap: spacing.small_12,
        },
        primaryMobileFooterButtons: {
            display: "grid",
            gap: spacing.small_12,
            alignItems: "center",
        },
        primaryMobileFooterButtonsWithSaveButton: {
            gridTemplateColumns: "1fr 1fr auto",
        },
        primaryMobileFooterButtonsWithoutSaveButton: {
            gridTemplateColumns: "1fr auto",
        },
        tooltipContent: {
            maxWidth: 234,
        },
    });

const embeddedStyles = getStyles(true);
const standaloneStyles = getStyles(false);
